import { useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "./queryClient";
import { AuthContext, type User, type AuthContextType } from "./auth-context";

export type { User, AuthContextType };
export { AuthContext };

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: user, isLoading } = useQuery<User | null>({
    queryKey: ["/api/auth/me"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/auth/me", { credentials: "include" });
        if (!res.ok) return null;
        return res.json();
      } catch {
        return null;
      }
    },
    staleTime: Infinity,
    retry: false,
  });

  const login = async (username: string, password: string) => {
    const res = await apiRequest("POST", "/api/auth/login", { username, password });
    const data = await res.json();
    if (data.requiresTwoFactor) {
      throw { requiresTwoFactor: true, userId: data.userId };
    }
    queryClient.setQueryData(["/api/auth/me"], data);
    return data;
  };

  const register = async (formData: any) => {
    const res = await apiRequest("POST", "/api/auth/register", formData);
    const data = await res.json();
    queryClient.setQueryData(["/api/auth/me"], data);
    return data;
  };

  const logout = async () => {
    await apiRequest("POST", "/api/auth/logout");
    queryClient.setQueryData(["/api/auth/me"], null);
    queryClient.clear();
  };

  return (
    <AuthContext.Provider value={{ user: user ?? null, isLoading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
